import { Box, Stack, styled } from "@mui/material";
import { useTheme } from "@mui/system";
import { useTranslation } from "react-i18next";
import { RoutePaths } from "constants/RoutePaths";
import { useContext, useState } from "react";
import { MyTrainingsViewContext } from "context/myTrainings/MyTrainingsViewContext";
import { EmptyUserAvatar } from "components/EmptyUserAvatar/EmptyUserAvatar";
import { useNavigate } from "react-router-dom";
import {
  WelcomeTypography,
  SubtitleTypography,
  EditProfileButton,
} from "./HeaderSection.styles";
import { LoadingButton } from "@mui/lab";
import { QrCodeLoginModal } from "components/QrCodeLoginModal";

const StyledUserAvatar = styled("img")({
  height: "80px",
  width: "80px",
  borderRadius: "100%",
  objectFit: "cover",
});

const StyledHeaderBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  paddingBottom: theme.spacing(4),
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.spacing(2),
  height: "auto",
  flexWrap: "wrap",
  width: "100%",
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    alignItems: "flex-end",
  },
}));

const StyledButtonStack = styled(Stack)(({ theme }) => ({
  width: "100%",
  justifyContent: "flex-end",
  flexWrap: "wrap",
  "& > *": {
    whiteSpace: "nowrap",
  },
  [theme.breakpoints.up("md")]: {
    width: "auto",
    justifyContent: "flex-start",
  },
}));

const StyledLoadingButton = styled(LoadingButton)({
  whiteSpace: "nowrap",
});

export const HeaderSection = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, isStatisticOfLoggedUser } = useContext(MyTrainingsViewContext);

  const [qrModalOpen, setQrModalOpen] = useState(false);

  const openModalWithQrCode = () => {
    setQrModalOpen(true);
  };

  const handleEditProfileClick = () => {
    navigate(
      isStatisticOfLoggedUser
        ? RoutePaths.PROFILE
        : `${RoutePaths.PROFILE}?userId=${user?.id}`,
    );
  };

  return !user ? null : (
    <StyledHeaderBox data-testid="my-trainings-header-section">
      <Stack direction="row" alignItems="center" spacing={6}>
        {user.profilePhotoUrl ? (
          <StyledUserAvatar src={user.profilePhotoUrl} alt="User Avatar" />
        ) : (
          <EmptyUserAvatar color="black" fontSize="3em" padding="2" />
        )}

        <Stack direction="column" spacing={1}>
          <WelcomeTypography variant="h5">
            {isStatisticOfLoggedUser
              ? t("myTrainings.welcomeUser", { userName: user.displayName })
              : t("myTrainings.welcomeInUserProgress", {
                  userName: user.displayName,
                })}
          </WelcomeTypography>
          <SubtitleTypography variant="h6">
            {isStatisticOfLoggedUser
              ? t("myTrainings.checkYourLessons")
              : t("myTrainings.checkUserProgress")}
          </SubtitleTypography>
        </Stack>
      </Stack>

      <StyledButtonStack direction="row" spacing={1}>
        <EditProfileButton onClick={handleEditProfileClick} variant="outlined">
          {t("myTrainings.editProfile")}
        </EditProfileButton>

        <StyledLoadingButton
          onClick={openModalWithQrCode}
          color={theme.palette.main}
          variant="contained"
        >
          {t("qrModal.loginToQr")}
        </StyledLoadingButton>
      </StyledButtonStack>

      <QrCodeLoginModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
      />
    </StyledHeaderBox>
  );
};
